"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Button, Modal, ModalOverlay, Dialog } from "react-aria-components";
import { useOptimizedFilters } from "@/hooks/useOptimizedFilters";
import { sortings } from "@/utils/SortingUtil";
import CopyToClipboard from "../common/clipboard/CopyToClipboard";
import { MakeModelSelector } from "./filters/MakeModelSelector";
import { BodyStyleFilter } from "./filters/BodyStyleFilter";
import { RangeFilter } from "./filters/RangeFilter";
import { QuickRangeFilter } from "./filters/QuickRangeFilter";
import { ButtonGroupFilter } from "./filters/ButtonGroupFilter";
import { ColorFilter } from "./filters/ColorFilter";
import { MobileFilterModal } from "./filters/MobileFilterModal";
import { useMobileFilter } from "./MobileFilterProvider";
import type { SearchSideProps } from "@/types/searchSide";
import QuickFilters from "./QuickFilters";
import ShopByPaymentCard from "./ShopByPaymentCard";
import CollectionSidebarLinks from "@/components/collections/CollectionSidebarLinks";
import type { SortOption } from "@/types/cars";
interface FilterSection {
  id: string;
  isOpen: boolean;
}
const SidebarHeader: React.FC<{
  onClear: () => void;
  hasActiveFilters: boolean;
}> = t0 => {
  const $ = _c(6);
  const {
    onClear,
    hasActiveFilters
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div><span className="t-mb-1 t-block t-text-[11px] t-font-black t-uppercase t-tracking-[0.14em] t-text-brand-violet">Filters</span><strong className="t-block t-text-xl t-font-bold t-leading-tight t-text-automotive-dark">Shop your way.</strong></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== hasActiveFilters || $[2] !== onClear) {
    t2 = hasActiveFilters && <button type="button" onClick={onClear} className="t-rounded-none t-text-xs t-font-extrabold t-text-brand-violet hover:t-text-brand-violet-dark focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2" aria-label="Clear all filters">Clear all</button>;
    $[1] = hasActiveFilters;
    $[2] = onClear;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <header className="t-flex t-items-end t-justify-between t-gap-4 t-border-b t-border-[#e4e0e6] t-bg-white t-px-6 t-pb-[22px] t-pt-[27px]">{t1}{t2}</header>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

// Modern Sort Button Component with consistent styling
const ModernSortButton: React.FC<{
  onClick: () => void;
}> = t0 => {
  const $ = _c(4);
  const {
    onClick
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <svg className="t-w-4 t-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" /></svg>;
    t2 = <span>Sort</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onClick) {
    t3 = <Button onPress={onClick} className="t-inline-flex t-items-center t-gap-2 t-px-4 t-py-2 t-bg-ecommerce-card t-text-ecommerce-text t-border t-border-ecommerce-border t-rounded-none t-text-sm t-font-medium hover:t-bg-ecommerce-hover hover:t-border-automotive-primary active:t-bg-ecommerce-hover-dark pressed:t-bg-ecommerce-hover-dark t-transition-all t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 t-shadow-card" aria-label="Sort results">{t1}{t2}</Button>;
    $[2] = onClick;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
const SearchSideOptimized: React.FC<SearchSideProps> = props => {
  const $ = _c(270);
  const {
    showMobileFilters,
    setShowMobileFilters
  } = useMobileFilter();
  const [showSort, setShowSort] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [{
      id: "makeModel",
      isOpen: true
    }, {
      id: "bodyStyle",
      isOpen: false
    }, {
      id: "year",
      isOpen: false
    }, {
      id: "mileage",
      isOpen: false
    }, {
      id: "price",
      isOpen: true
    }, {
      id: "driveTrain",
      isOpen: false
    }, {
      id: "cylinder",
      isOpen: false
    }, {
      id: "transmission",
      isOpen: false
    }, {
      id: "color",
      isOpen: false
    }, {
      id: "fuel",
      isOpen: false
    }, {
      id: "doors",
      isOpen: false
    }];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [filterSections, setFilterSections] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== showMobileFilters) {
    t1 = () => {
      if (showMobileFilters) {
        setFilterSections(_temp2);
      }
    };
    t2 = [showMobileFilters];
    $[1] = showMobileFilters;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const {
    hooks,
    rangeBounds,
    makesData,
    expandedMakes,
    currentValues,
    hasActiveFilters,
    clearAllFilters,
    formatCurrency,
    formatNumber,
    handleMakeChange,
    handleModelChange,
    handleMakeModelChange,
    handleMakeToggle,
    handleClearMakes,
    handleClearModels,
    handleClearMakeModels,
    trackFilterChange
  } = useOptimizedFilters(props);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = sectionId => {
      setFilterSections(prev => prev.map(section_0 => section_0.id === sectionId ? {
        ...section_0,
        isOpen: !section_0.isOpen
      } : section_0));
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const toggleFilterSection = t3;
  let t4;
  if ($[5] !== filterSections) {
    t4 = sectionId_0 => filterSections.find(section_1 => section_1.id === sectionId_0)?.isOpen ?? false;
    $[5] = filterSections;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const getFilterSectionState = t4;
  let t5;
  if ($[7] !== hooks || $[8] !== trackFilterChange) {
    t5 = values => {
      hooks.setSelectedBodyStyles(values);
      trackFilterChange("body_style", values);
    };
    $[7] = hooks;
    $[8] = trackFilterChange;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleBodyStyleChange = t5;
  let t6;
  if ($[10] !== hooks || $[11] !== trackFilterChange) {
    t6 = values_0 => {
      hooks.setYearRange(values_0);
      trackFilterChange("year_range", values_0);
    };
    $[10] = hooks;
    $[11] = trackFilterChange;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleYearRangeChange = t6;
  let t7;
  if ($[13] !== hooks || $[14] !== trackFilterChange) {
    t7 = values_1 => {
      hooks.setMileageRange(values_1);
      trackFilterChange("mileage_range", values_1);
    };
    $[13] = hooks;
    $[14] = trackFilterChange;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleMileageRangeChange = t7;
  let t8;
  if ($[16] !== hooks || $[17] !== trackFilterChange) {
    t8 = values_2 => {
      hooks.setPriceRange(values_2);
      trackFilterChange("price_range", values_2);
    };
    $[16] = hooks;
    $[17] = trackFilterChange;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handlePriceRangeChange = t8;
  let t9;
  if ($[19] !== hooks || $[20] !== trackFilterChange) {
    t9 = values_3 => {
      hooks.setSelectedDriveTrains(values_3);
      trackFilterChange("drive_train", values_3);
    };
    $[19] = hooks;
    $[20] = trackFilterChange;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleDriveTrainChange = t9;
  let t10;
  if ($[22] !== hooks || $[23] !== trackFilterChange) {
    t10 = values_4 => {
      hooks.setSelectedCylinders(values_4);
      trackFilterChange("cylinder", values_4);
    };
    $[22] = hooks;
    $[23] = trackFilterChange;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleCylinderChange = t10;
  let t11;
  if ($[25] !== hooks || $[26] !== trackFilterChange) {
    t11 = values_5 => {
      hooks.setSelectedTransmissions(values_5);
      trackFilterChange("transmission", values_5);
    };
    $[25] = hooks;
    $[26] = trackFilterChange;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const handleTransmissionChange = t11;
  let t12;
  if ($[28] !== hooks || $[29] !== trackFilterChange) {
    t12 = values_6 => {
      hooks.setSelectedFuelTypes(values_6);
      trackFilterChange("fuel_type", values_6);
    };
    $[28] = hooks;
    $[29] = trackFilterChange;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const handleFuelTypeChange = t12;
  let t13;
  if ($[31] !== hooks || $[32] !== trackFilterChange) {
    t13 = values_7 => {
      hooks.setSelectedDoors(values_7);
      trackFilterChange("doors", values_7);
    };
    $[31] = hooks;
    $[32] = trackFilterChange;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const handleDoorsChange = t13;
  let t14;
  if ($[34] !== hooks || $[35] !== trackFilterChange) {
    t14 = values_8 => {
      hooks.setSelectedInteriorColors(values_8);
      trackFilterChange("interior_color", values_8);
    };
    $[34] = hooks;
    $[35] = trackFilterChange;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const handleInteriorColorChange = t14;
  let t15;
  if ($[37] !== hooks || $[38] !== trackFilterChange) {
    t15 = values_9 => {
      hooks.setSelectedExteriorColors(values_9);
      trackFilterChange("exterior_color", values_9);
    };
    $[37] = hooks;
    $[38] = trackFilterChange;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const handleExteriorColorChange = t15;
  let t16;
  if ($[40] !== handleMakeChange || $[41] !== trackFilterChange) {
    t16 = values_10 => {
      handleMakeChange(values_10);
      trackFilterChange("make", values_10);
    };
    $[40] = handleMakeChange;
    $[41] = trackFilterChange;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const handleMakeChangeWithTracking = t16;
  let t17;
  if ($[43] !== handleModelChange || $[44] !== trackFilterChange) {
    t17 = values_11 => {
      handleModelChange(values_11);
      trackFilterChange("model", values_11);
    };
    $[43] = handleModelChange;
    $[44] = trackFilterChange;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const handleModelChangeWithTracking = t17;
  let t18;
  if ($[46] !== handleMakeModelChange || $[47] !== trackFilterChange) {
    t18 = (makes, models) => {
      handleMakeModelChange(makes, models);
      trackFilterChange("make", makes);
      trackFilterChange("model", models);
    };
    $[46] = handleMakeModelChange;
    $[47] = trackFilterChange;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const handleMakeModelChangeWithTracking = t18;
  const t19 = rangeBounds.minPrice;
  const t20 = rangeBounds.maxPrice;
  const t21 = currentValues.priceRange;
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = [20000, 25000, 35000, 50000, 75000];
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== hooks || $[51] !== rangeBounds.maxPrice || $[52] !== rangeBounds.minPrice) {
    t23 = () => hooks.setPriceRange([rangeBounds.minPrice, rangeBounds.maxPrice]);
    $[50] = hooks;
    $[51] = rangeBounds.maxPrice;
    $[52] = rangeBounds.minPrice;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== getFilterSectionState) {
    t24 = getFilterSectionState("price");
    $[54] = getFilterSectionState;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => toggleFilterSection("price");
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== currentValues.priceRange || $[58] !== formatCurrency || $[59] !== handlePriceRangeChange || $[60] !== rangeBounds.maxPrice || $[61] !== rangeBounds.minPrice || $[62] !== t23 || $[63] !== t24) {
    t26 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><QuickRangeFilter title="Price" min={t19} max={t20} value={t21} limits={t22} formatLimit={_temp3} anyLabel="Any price" onChange={handlePriceRangeChange} onClear={t23} formatValue={formatCurrency} isOpen={t24} onToggle={t25} aria-label="Select vehicle price range" /></div>;
    $[57] = currentValues.priceRange;
    $[58] = formatCurrency;
    $[59] = handlePriceRangeChange;
    $[60] = rangeBounds.maxPrice;
    $[61] = rangeBounds.minPrice;
    $[62] = t23;
    $[63] = t24;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  const t27 = currentValues.selectedMakes;
  const t28 = currentValues.selectedModels;
  const t29 = props.allModels;
  let t30;
  if ($[65] !== getFilterSectionState) {
    t30 = getFilterSectionState("makeModel");
    $[65] = getFilterSectionState;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => toggleFilterSection("makeModel");
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== currentValues.selectedMakes || $[69] !== currentValues.selectedModels || $[70] !== expandedMakes || $[71] !== handleClearMakeModels || $[72] !== handleClearMakes || $[73] !== handleClearModels || $[74] !== handleMakeChangeWithTracking || $[75] !== handleMakeModelChangeWithTracking || $[76] !== handleMakeToggle || $[77] !== handleModelChangeWithTracking || $[78] !== makesData || $[79] !== props.allModels || $[80] !== t30) {
    t32 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><MakeModelSelector makes={makesData} selectedMakes={t27} selectedModels={t28} allModels={t29} expandedMakes={expandedMakes} onMakeChange={handleMakeChangeWithTracking} onModelChange={handleModelChangeWithTracking} onMakeModelChange={handleMakeModelChangeWithTracking} onMakeToggle={handleMakeToggle} onClearMakes={handleClearMakes} onClearModels={handleClearModels} onClearMakeModels={handleClearMakeModels} isOpen={t30} onToggle={t31} aria-label="Select vehicle make and model" /></div>;
    $[68] = currentValues.selectedMakes;
    $[69] = currentValues.selectedModels;
    $[70] = expandedMakes;
    $[71] = handleClearMakeModels;
    $[72] = handleClearMakes;
    $[73] = handleClearModels;
    $[74] = handleMakeChangeWithTracking;
    $[75] = handleMakeModelChangeWithTracking;
    $[76] = handleMakeToggle;
    $[77] = handleModelChangeWithTracking;
    $[78] = makesData;
    $[79] = props.allModels;
    $[80] = t30;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  const t33 = props.facets.bodyStyle;
  const t34 = currentValues.selectedBodyStyles;
  let t35;
  if ($[82] !== hooks) {
    t35 = () => hooks.setSelectedBodyStyles([]);
    $[82] = hooks;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== getFilterSectionState) {
    t36 = getFilterSectionState("bodyStyle");
    $[84] = getFilterSectionState;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => toggleFilterSection("bodyStyle");
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== currentValues.selectedBodyStyles || $[88] !== handleBodyStyleChange || $[89] !== props.facets.bodyStyle || $[90] !== t35 || $[91] !== t36) {
    t38 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><BodyStyleFilter availableBodyStyles={t33} selectedBodyStyles={t34} onBodyStyleChange={handleBodyStyleChange} onClear={t35} isOpen={t36} onToggle={t37} aria-label="Select vehicle body style" /></div>;
    $[87] = currentValues.selectedBodyStyles;
    $[88] = handleBodyStyleChange;
    $[89] = props.facets.bodyStyle;
    $[90] = t35;
    $[91] = t36;
    $[92] = t38;
  } else {
    t38 = $[92];
  }
  const t39 = rangeBounds.minYear;
  const t40 = rangeBounds.maxYear;
  const t41 = currentValues.yearRange;
  let t42;
  if ($[93] !== hooks || $[94] !== rangeBounds.maxYear || $[95] !== rangeBounds.minYear) {
    t42 = () => hooks.setYearRange([rangeBounds.minYear, rangeBounds.maxYear]);
    $[93] = hooks;
    $[94] = rangeBounds.maxYear;
    $[95] = rangeBounds.minYear;
    $[96] = t42;
  } else {
    t42 = $[96];
  }
  let t43;
  if ($[97] !== getFilterSectionState) {
    t43 = getFilterSectionState("year");
    $[97] = getFilterSectionState;
    $[98] = t43;
  } else {
    t43 = $[98];
  }
  let t44;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = () => toggleFilterSection("year");
    $[99] = t44;
  } else {
    t44 = $[99];
  }
  let t45;
  if ($[100] !== currentValues.yearRange || $[101] !== formatNumber || $[102] !== handleYearRangeChange || $[103] !== rangeBounds.maxYear || $[104] !== rangeBounds.minYear || $[105] !== t42 || $[106] !== t43) {
    t45 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><RangeFilter title="Year" min={t39} max={t40} value={t41} step={1} onChange={handleYearRangeChange} onClear={t42} formatValue={formatNumber} isOpen={t43} onToggle={t44} aria-label="Select vehicle year range" /></div>;
    $[100] = currentValues.yearRange;
    $[101] = formatNumber;
    $[102] = handleYearRangeChange;
    $[103] = rangeBounds.maxYear;
    $[104] = rangeBounds.minYear;
    $[105] = t42;
    $[106] = t43;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  const t46 = rangeBounds.minMileage;
  const t47 = rangeBounds.maxMileage;
  const t48 = currentValues.mileageRange;
  let t49;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = [30000, 50000, 75000, 100000, 150000];
    $[108] = t49;
  } else {
    t49 = $[108];
  }
  let t50;
  if ($[109] !== hooks || $[110] !== rangeBounds.maxMileage || $[111] !== rangeBounds.minMileage) {
    t50 = () => hooks.setMileageRange([rangeBounds.minMileage, rangeBounds.maxMileage]);
    $[109] = hooks;
    $[110] = rangeBounds.maxMileage;
    $[111] = rangeBounds.minMileage;
    $[112] = t50;
  } else {
    t50 = $[112];
  }
  let t51;
  if ($[113] !== getFilterSectionState) {
    t51 = getFilterSectionState("mileage");
    $[113] = getFilterSectionState;
    $[114] = t51;
  } else {
    t51 = $[114];
  }
  let t52;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = () => toggleFilterSection("mileage");
    $[115] = t52;
  } else {
    t52 = $[115];
  }
  let t53;
  if ($[116] !== currentValues.mileageRange || $[117] !== formatNumber || $[118] !== handleMileageRangeChange || $[119] !== rangeBounds.maxMileage || $[120] !== rangeBounds.minMileage || $[121] !== t50 || $[122] !== t51) {
    t53 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><QuickRangeFilter title="Mileage" min={t46} max={t47} value={t48} limits={t49} formatLimit={_temp4} anyLabel="Any mileage" onChange={handleMileageRangeChange} onClear={t50} formatValue={formatNumber} isOpen={t51} onToggle={t52} aria-label="Select vehicle mileage range" /></div>;
    $[116] = currentValues.mileageRange;
    $[117] = formatNumber;
    $[118] = handleMileageRangeChange;
    $[119] = rangeBounds.maxMileage;
    $[120] = rangeBounds.minMileage;
    $[121] = t50;
    $[122] = t51;
    $[123] = t53;
  } else {
    t53 = $[123];
  }
  let t54;
  if ($[124] !== props.facets.driveType) {
    t54 = props.facets?.driveType || [];
    $[124] = props.facets.driveType;
    $[125] = t54;
  } else {
    t54 = $[125];
  }
  const t55 = currentValues.selectedDriveTrains;
  let t56;
  if ($[126] !== hooks) {
    t56 = () => hooks.setSelectedDriveTrains([]);
    $[126] = hooks;
    $[127] = t56;
  } else {
    t56 = $[127];
  }
  let t57;
  if ($[128] !== getFilterSectionState) {
    t57 = getFilterSectionState("driveTrain");
    $[128] = getFilterSectionState;
    $[129] = t57;
  } else {
    t57 = $[129];
  }
  let t58;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = () => toggleFilterSection("driveTrain");
    $[130] = t58;
  } else {
    t58 = $[130];
  }
  let t59;
  if ($[131] !== currentValues.selectedDriveTrains || $[132] !== handleDriveTrainChange || $[133] !== t54 || $[134] !== t56 || $[135] !== t57) {
    t59 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><ButtonGroupFilter title="Drive Train" options={t54} selectedValues={t55} onChange={handleDriveTrainChange} onClear={t56} filterType="driveType" isOpen={t57} onToggle={t58} aria-label="Select vehicle drive train" /></div>;
    $[131] = currentValues.selectedDriveTrains;
    $[132] = handleDriveTrainChange;
    $[133] = t54;
    $[134] = t56;
    $[135] = t57;
    $[136] = t59;
  } else {
    t59 = $[136];
  }
  let t60;
  if ($[137] !== props.facets.cylinders) {
    t60 = props.facets?.cylinders || [];
    $[137] = props.facets.cylinders;
    $[138] = t60;
  } else {
    t60 = $[138];
  }
  const t61 = currentValues.selectedCylinders;
  let t62;
  if ($[139] !== hooks) {
    t62 = () => hooks.setSelectedCylinders([]);
    $[139] = hooks;
    $[140] = t62;
  } else {
    t62 = $[140];
  }
  let t63;
  if ($[141] !== getFilterSectionState) {
    t63 = getFilterSectionState("cylinder");
    $[141] = getFilterSectionState;
    $[142] = t63;
  } else {
    t63 = $[142];
  }
  let t64;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = () => toggleFilterSection("cylinder");
    $[143] = t64;
  } else {
    t64 = $[143];
  }
  let t65;
  if ($[144] !== currentValues.selectedCylinders || $[145] !== handleCylinderChange || $[146] !== t60 || $[147] !== t62 || $[148] !== t63) {
    t65 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><ButtonGroupFilter title="Cylinders" options={t60} selectedValues={t61} onChange={handleCylinderChange} onClear={t62} filterType="cylinder" isOpen={t63} onToggle={t64} aria-label="Select vehicle cylinders" /></div>;
    $[144] = currentValues.selectedCylinders;
    $[145] = handleCylinderChange;
    $[146] = t60;
    $[147] = t62;
    $[148] = t63;
    $[149] = t65;
  } else {
    t65 = $[149];
  }
  let t66;
  if ($[150] !== props.facets.transmission) {
    t66 = props.facets?.transmission || [];
    $[150] = props.facets.transmission;
    $[151] = t66;
  } else {
    t66 = $[151];
  }
  const t67 = currentValues.selectedTransmissions;
  let t68;
  if ($[152] !== hooks) {
    t68 = () => hooks.setSelectedTransmissions([]);
    $[152] = hooks;
    $[153] = t68;
  } else {
    t68 = $[153];
  }
  let t69;
  if ($[154] !== getFilterSectionState) {
    t69 = getFilterSectionState("transmission");
    $[154] = getFilterSectionState;
    $[155] = t69;
  } else {
    t69 = $[155];
  }
  let t70;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = () => toggleFilterSection("transmission");
    $[156] = t70;
  } else {
    t70 = $[156];
  }
  let t71;
  if ($[157] !== currentValues.selectedTransmissions || $[158] !== handleTransmissionChange || $[159] !== t66 || $[160] !== t68 || $[161] !== t69) {
    t71 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><ButtonGroupFilter title="Transmission" options={t66} selectedValues={t67} onChange={handleTransmissionChange} onClear={t68} filterType="transmission" isOpen={t69} onToggle={t70} aria-label="Select vehicle transmission" /></div>;
    $[157] = currentValues.selectedTransmissions;
    $[158] = handleTransmissionChange;
    $[159] = t66;
    $[160] = t68;
    $[161] = t69;
    $[162] = t71;
  } else {
    t71 = $[162];
  }
  const t72 = currentValues.selectedInteriorColors;
  const t73 = currentValues.selectedExteriorColors;
  let t74;
  if ($[163] !== props.facets.inColor) {
    t74 = props.facets?.inColor || [];
    $[163] = props.facets.inColor;
    $[164] = t74;
  } else {
    t74 = $[164];
  }
  let t75;
  if ($[165] !== props.facets.exColor) {
    t75 = props.facets?.exColor || [];
    $[165] = props.facets.exColor;
    $[166] = t75;
  } else {
    t75 = $[166];
  }
  let t76;
  if ($[167] !== hooks) {
    t76 = () => {
      hooks.setSelectedInteriorColors([]);
      hooks.setSelectedExteriorColors([]);
    };
    $[167] = hooks;
    $[168] = t76;
  } else {
    t76 = $[168];
  }
  let t77;
  if ($[169] !== getFilterSectionState) {
    t77 = getFilterSectionState("color");
    $[169] = getFilterSectionState;
    $[170] = t77;
  } else {
    t77 = $[170];
  }
  let t78;
  if ($[171] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = () => toggleFilterSection("color");
    $[171] = t78;
  } else {
    t78 = $[171];
  }
  let t79;
  if ($[172] !== currentValues.selectedExteriorColors || $[173] !== currentValues.selectedInteriorColors || $[174] !== handleExteriorColorChange || $[175] !== handleInteriorColorChange || $[176] !== t74 || $[177] !== t75 || $[178] !== t76 || $[179] !== t77) {
    t79 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><ColorFilter interiorColors={t72} exteriorColors={t73} interiorOptions={t74} exteriorOptions={t75} onInteriorColorChange={handleInteriorColorChange} onExteriorColorChange={handleExteriorColorChange} onClear={t76} isOpen={t77} onToggle={t78} aria-label="Select vehicle color" /></div>;
    $[172] = currentValues.selectedExteriorColors;
    $[173] = currentValues.selectedInteriorColors;
    $[174] = handleExteriorColorChange;
    $[175] = handleInteriorColorChange;
    $[176] = t74;
    $[177] = t75;
    $[178] = t76;
    $[179] = t77;
    $[180] = t79;
  } else {
    t79 = $[180];
  }
  let t80;
  if ($[181] !== props.facets.fuelType) {
    t80 = props.facets?.fuelType || [];
    $[181] = props.facets.fuelType;
    $[182] = t80;
  } else {
    t80 = $[182];
  }
  const t81 = currentValues.selectedFuelTypes;
  let t82;
  if ($[183] !== hooks) {
    t82 = () => hooks.setSelectedFuelTypes([]);
    $[183] = hooks;
    $[184] = t82;
  } else {
    t82 = $[184];
  }
  let t83;
  if ($[185] !== getFilterSectionState) {
    t83 = getFilterSectionState("fuel");
    $[185] = getFilterSectionState;
    $[186] = t83;
  } else {
    t83 = $[186];
  }
  let t84;
  if ($[187] === Symbol.for("react.memo_cache_sentinel")) {
    t84 = () => toggleFilterSection("fuel");
    $[187] = t84;
  } else {
    t84 = $[187];
  }
  let t85;
  if ($[188] !== currentValues.selectedFuelTypes || $[189] !== handleFuelTypeChange || $[190] !== t80 || $[191] !== t82 || $[192] !== t83) {
    t85 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><ButtonGroupFilter title="Fuel Type" options={t80} selectedValues={t81} onChange={handleFuelTypeChange} onClear={t82} isOpen={t83} onToggle={t84} aria-label="Select vehicle fuel type" /></div>;
    $[188] = currentValues.selectedFuelTypes;
    $[189] = handleFuelTypeChange;
    $[190] = t80;
    $[191] = t82;
    $[192] = t83;
    $[193] = t85;
  } else {
    t85 = $[193];
  }
  let t86;
  if ($[194] !== props.facets.doors) {
    t86 = props.facets?.doors || [];
    $[194] = props.facets.doors;
    $[195] = t86;
  } else {
    t86 = $[195];
  }
  const t87 = currentValues.selectedDoors;
  let t88;
  if ($[196] !== hooks) {
    t88 = () => hooks.setSelectedDoors([]);
    $[196] = hooks;
    $[197] = t88;
  } else {
    t88 = $[197];
  }
  let t89;
  if ($[198] !== getFilterSectionState) {
    t89 = getFilterSectionState("doors");
    $[198] = getFilterSectionState;
    $[199] = t89;
  } else {
    t89 = $[199];
  }
  let t90;
  if ($[200] === Symbol.for("react.memo_cache_sentinel")) {
    t90 = () => toggleFilterSection("doors");
    $[200] = t90;
  } else {
    t90 = $[200];
  }
  let t91;
  if ($[201] !== currentValues.selectedDoors || $[202] !== handleDoorsChange || $[203] !== t86 || $[204] !== t88 || $[205] !== t89) {
    t91 = <div className="t-px-5 t-py-[18px] sm:t-px-6"><ButtonGroupFilter title="Doors" options={t86} selectedValues={t87} onChange={handleDoorsChange} onClear={t88} isOpen={t89} onToggle={t90} aria-label="Select number of doors" /></div>;
    $[201] = currentValues.selectedDoors;
    $[202] = handleDoorsChange;
    $[203] = t86;
    $[204] = t88;
    $[205] = t89;
    $[206] = t91;
  } else {
    t91 = $[206];
  }
  let t92;
  if ($[207] !== t26 || $[208] !== t32 || $[209] !== t38 || $[210] !== t45 || $[211] !== t53 || $[212] !== t59 || $[213] !== t65 || $[214] !== t71 || $[215] !== t79 || $[216] !== t85 || $[217] !== t91) {
    t92 = <div className="t-divide-y t-divide-[#e4e0e6] t-pb-8">{t26}{t32}{t38}{t45}{t53}{t59}{t65}{t71}{t79}{t85}{t91}</div>;
    $[207] = t26;
    $[208] = t32;
    $[209] = t38;
    $[210] = t45;
    $[211] = t53;
    $[212] = t59;
    $[213] = t65;
    $[214] = t71;
    $[215] = t79;
    $[216] = t85;
    $[217] = t91;
    $[218] = t92;
  } else {
    t92 = $[218];
  }
  const FilterContent = t92;
  let t93;
  if ($[219] !== clearAllFilters || $[220] !== hasActiveFilters) {
    t93 = <SidebarHeader onClear={clearAllFilters} hasActiveFilters={hasActiveFilters} />;
    $[219] = clearAllFilters;
    $[220] = hasActiveFilters;
    $[221] = t93;
  } else {
    t93 = $[221];
  }
  let t94;
  if ($[222] !== props.collections) {
    t94 = props.collections || [];
    $[222] = props.collections;
    $[223] = t94;
  } else {
    t94 = $[223];
  }
  let t95;
  if ($[224] !== props.currentCollectionSlug || $[225] !== t94) {
    t95 = <CollectionSidebarLinks collections={t94} currentSlug={props.currentCollectionSlug} />;
    $[224] = props.currentCollectionSlug;
    $[225] = t94;
    $[226] = t95;
  } else {
    t95 = $[226];
  }
  let t96;
  if ($[227] !== FilterContent || $[228] !== t93 || $[229] !== t95) {
    t96 = <div className="t-w-full t-bg-white">{t93}{t95}{FilterContent}</div>;
    $[227] = FilterContent;
    $[228] = t93;
    $[229] = t95;
    $[230] = t96;
  } else {
    t96 = $[230];
  }
  const DesktopFilterContainer = t96;
  const t97 = currentValues.yearRange?.length > 0 && DesktopFilterContainer;
  let t98;
  if ($[231] !== t97) {
    t98 = <div className="t-hidden lg:t-block">{t97}</div>;
    $[231] = t97;
    $[232] = t98;
  } else {
    t98 = $[232];
  }
  let t99;
  if ($[233] !== setShowMobileFilters) {
    t99 = () => setShowMobileFilters(false);
    $[233] = setShowMobileFilters;
    $[234] = t99;
  } else {
    t99 = $[234];
  }
  const t100 = props.carsCount || 0;
  let t101;
  if ($[235] === Symbol.for("react.memo_cache_sentinel")) {
    t101 = <div className="t-mb-4"><h3 className="t-mb-3 t-text-sm t-font-semibold">Quick filters</h3><QuickFilters className="t-flex-wrap" /></div>;
    $[235] = t101;
  } else {
    t101 = $[235];
  }
  let t102;
  if ($[236] !== setShowMobileFilters) {
    t102 = <div className="t-mb-4"><ShopByPaymentCard compact={true} onOpen={() => setShowMobileFilters(false)} /></div>;
    $[236] = setShowMobileFilters;
    $[237] = t102;
  } else {
    t102 = $[237];
  }
  let t103;
  if ($[238] !== props.collections) {
    t103 = props.collections || [];
    $[238] = props.collections;
    $[239] = t103;
  } else {
    t103 = $[239];
  }
  let t104;
  if ($[240] !== setShowMobileFilters) {
    t104 = () => setShowMobileFilters(false);
    $[240] = setShowMobileFilters;
    $[241] = t104;
  } else {
    t104 = $[241];
  }
  let t105;
  if ($[242] !== props.currentCollectionSlug || $[243] !== t103 || $[244] !== t104) {
    t105 = <CollectionSidebarLinks collections={t103} currentSlug={props.currentCollectionSlug} onNavigate={t104} />;
    $[242] = props.currentCollectionSlug;
    $[243] = t103;
    $[244] = t104;
    $[245] = t105;
  } else {
    t105 = $[245];
  }
  let t106;
  if ($[246] !== FilterContent || $[247] !== t105) {
    t106 = <div className="t-bg-ecommerce-card t-rounded-automotive t-shadow-automotive t-border t-border-ecommerce-border t-overflow-hidden">{t105}{FilterContent}</div>;
    $[246] = FilterContent;
    $[247] = t105;
    $[248] = t106;
  } else {
    t106 = $[248];
  }
  let t107;
  if ($[249] !== clearAllFilters || $[250] !== hasActiveFilters || $[251] !== showMobileFilters || $[252] !== t100 || $[253] !== t102 || $[254] !== t106 || $[255] !== t99) {
    t107 = <MobileFilterModal isOpen={showMobileFilters} onClose={t99} carsCount={t100} hasActiveFilters={hasActiveFilters} onClearAll={clearAllFilters}>{t101}{t102}{t106}</MobileFilterModal>;
    $[249] = clearAllFilters;
    $[250] = hasActiveFilters;
    $[251] = showMobileFilters;
    $[252] = t100;
    $[253] = t102;
    $[254] = t106;
    $[255] = t99;
    $[256] = t107;
  } else {
    t107 = $[256];
  }
  let t108;
  if ($[257] === Symbol.for("react.memo_cache_sentinel")) {
    t108 = <CopyToClipboard />;
    $[257] = t108;
  } else {
    t108 = $[257];
  }
  let t109;
  if ($[258] !== showSort) {
    t109 = <div className="t-flex t-items-center t-gap-3">{t108}<ModernSortButton onClick={() => setShowSort(!showSort)} /></div>;
    $[258] = showSort;
    $[259] = t109;
  } else {
    t109 = $[259];
  }
  let t110;
  if ($[260] !== t107 || $[261] !== t109) {
    t110 = <div className="lg:t-hidden"><div className="t-flex t-items-center t-justify-between t-p-4 t-bg-ecommerce-card t-border-b t-border-ecommerce-border">{t107}{t109}</div></div>;
    $[260] = t107;
    $[261] = t109;
    $[262] = t110;
  } else {
    t110 = $[262];
  }
  let t111;
  if ($[263] !== hooks || $[264] !== showSort) {
    t111 = showSort && <ModalOverlay className="t-fixed t-inset-0 t-z-50 t-flex t-items-center t-justify-center t-bg-black/50 t-backdrop-blur-sm" isDismissable={true} onOpenChange={setShowSort} isOpen={showSort}><Modal className="t-bg-ecommerce-card t-rounded-automotive t-shadow-elegant t-max-w-sm t-w-full t-mx-4 t-overflow-hidden"><Dialog className="focus:t-outline-none">{t112 => {
            const {
              close
            } = t112;
            return <><header className="t-flex t-items-center t-justify-between t-py-4 t-px-6 t-border-b t-border-ecommerce-border"><h3 className="t-text-lg t-font-semibold t-text-ecommerce-text">Sort Results</h3><Button onPress={close} className="t-p-2 t-text-ecommerce-text-muted hover:t-text-ecommerce-text hover:t-bg-ecommerce-hover t-rounded-none t-transition-colors focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary" aria-label="Close sort modal"><svg className="t-w-5 t-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></Button></header><div className="t-p-4 t-space-y-2">{sortings.map((sortingOption, index) => <Button key={index} onPress={() => {
                  hooks.setSortOption(sortingOption.value as SortOption);
                  close();
                }} className="t-w-full t-text-left t-px-4 t-py-3 t-text-ecommerce-text t-border t-border-ecommerce-border t-rounded-none hover:t-bg-ecommerce-hover hover:t-border-automotive-primary active:t-bg-ecommerce-hover-dark pressed:t-bg-ecommerce-hover-dark t-transition-all t-duration-200 focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2" aria-label={`Sort by ${sortingOption.label}`}><span className="t-font-medium">{sortingOption.label}</span></Button>)}</div></>;
          }}</Dialog></Modal></ModalOverlay>;
    $[263] = hooks;
    $[264] = showSort;
    $[265] = t111;
  } else {
    t111 = $[265];
  }
  let t112;
  if ($[266] !== t110 || $[267] !== t111 || $[268] !== t98) {
    t112 = <div className="t-w-full">{t98}{t110}{t111}</div>;
    $[266] = t110;
    $[267] = t111;
    $[268] = t98;
    $[269] = t112;
  } else {
    t112 = $[269];
  }
  return t112;
};
export default SearchSideOptimized;
function _temp(section) {
  return {
    ...section,
    isOpen: false
  };
}
function _temp2(sections) {
  return sections.map(_temp);
}
function _temp3(limit) {
  return `$${limit / 1000}k`;
}
function _temp4(limit_0) {
  return `${limit_0 / 1000}k mi`;
}